/**
 * The Bookings page (web-account-pages, signed-in-web.html): the client's
 * upcoming bookings and pending requests, then past and cancelled ones, a ruled
 * table each, every row opening that booking's page; Show earlier sessions
 * pages back through the history, as the app's Past list does.
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'bookings-list',
  description: "The client's bookings: upcoming and pending, then past and cancelled, each row opening its booking's page.",
  placeable: true,
  options: {},
  parts: {
    page: { description: 'The whole Bookings page.', required: false },
    title: { description: 'Bookings, and its line.', required: false },
    book: { description: 'Book a session, to the services.', required: false, role: 'secondary-action' },
    upcoming: { description: 'The upcoming table.', required: false },
    past: { description: 'The past table.', required: false },
    row: { description: "One booking: date, session, time, where, status and payment; opens the booking's page.", required: true, states: ['live', 'done', 'off'] },
    empty: { description: 'What a table says when it has no bookings.', required: false },
    more: { description: 'Show earlier sessions: the next page of history.', required: false, states: ['disabled'] },
    head: { description: 'The heading block: the title, its line and Book a session.', required: false },
    month: { description: "A row's month.", required: false },
    amount: { description: "A row's payment amount.", required: false },
    error: { description: 'What the upcoming table says when it could not be read.', required: false },
    retry: { description: 'Try again: reads the upcoming bookings again after they could not be read.', required: true },
  },
  actions: {
    loadMorePast: { description: 'Loads the next page of past bookings.', args: [] },
    retryUpcoming: { description: 'Reads the upcoming bookings again after they could not be read.', args: [] },
    open: { description: "Opens a page of the site (a booking's page, the services).", args: [{ name: 'href', kind: 'value', type: 'string', format: 'path' }] },
  },
  events: {},
});

/** One booking in a table. */
export interface BookingsListRow {
  /** The booking's id. */
  readonly id: string;
  /** The booking's page. */
  readonly href: string;
  /** "Oct". */
  readonly month: string;
  /** "24". */
  readonly day: string;
  /** "Sat", or "Today". */
  readonly weekday: string;
  /** True for a booking today. */
  readonly today: boolean;
  /** The service's name. */
  readonly name: string;
  /** "Group event" for an event, else null. */
  readonly kicker: string | null;
  /** "with Noor Hadley", or null. */
  readonly withName: string | null;
  /** The service's photo (upcoming rows), or null. */
  readonly photoUrl: string | null;
  /** website-photo-zoom: the same photo framed as the studio set it (crop point and zoom), or null. */
  readonly photo: SitePhoto | null;
  /** "2:00 PM". */
  readonly time: string;
  /** "75 min", or null when unknown. */
  readonly length: string | null;
  /** Online, or the place with its town or how many are going. */
  readonly place: { readonly online: boolean; readonly byPhone: boolean; readonly main: string; readonly sub: string | null };
  /** Confirmed, Pending approval, Payment due, Completed, Cancelled. */
  readonly status: string;
  /** live (accent dot), done (muted), off (cancelled ring). */
  readonly tone: 'live' | 'done' | 'off';
  /** The payment cell, or null when the read does not say. */
  readonly payment: { readonly amount: string | null; readonly note: string | null } | null;
}

/** What the Bookings page knows. */
export interface BookingsListState {
  /** loading until the upcoming bookings are read. */
  readonly status: 'loading' | 'ready';
  /** ready, or error when the upcoming bookings could not be read: status is then ready, the page is drawn with Try again, and the history still shows. */
  readonly upcomingStatus: 'ready' | 'error';
  /** Upcoming bookings and pending requests, soonest first. */
  readonly upcoming: readonly BookingsListRow[];
  /** Past and cancelled bookings, newest first, as far as read. */
  readonly past: readonly BookingsListRow[];
  /** loading, ready or error for the history. */
  readonly pastStatus: 'loading' | 'ready' | 'error';
  /** True when earlier history is left to read. */
  readonly hasMorePast: boolean;
  /** True while the next page of history is read. */
  readonly loadingMorePast: boolean;
  /** Where Book a session goes. */
  readonly bookHref: string;
}

/** What a drawing can ask the Bookings page to do. */
export interface BookingsListActions {
  /** Read the next page of past bookings. */
  readonly loadMorePast: () => void;
  /** Read the upcoming bookings again. */
  readonly retryUpcoming: () => void;
  /** Open a page of the site. */
  readonly open: (href: string) => void;
}

/** The Bookings page tells its host nothing. */
export type BookingsListEvents = Record<string, never>;
